import { useState } from "react";
import { X, MessageCircle, Upload, ImagePlus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { waLink } from "../data/studioProjects";
import { uploadStudioFile } from "../lib/api";

export const StudioBudgetModal = ({ open, onClose, projectRef }) => {
  const [business, setBusiness] = useState("");
  const [need, setNeed] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);

  if (!open) return null;

  const handleLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    try {
      setLogoUrl(await uploadStudioFile(f));
    } catch {
      toast.error("Logo upload failed");
    } finally {
      setLogoBusy(false);
    }
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotoBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        try { urls.push(await uploadStudioFile(f)); } catch { /* skip */ }
      }
      setPhotos((p) => [...p, ...urls]);
    } finally {
      setPhotoBusy(false);
    }
  };

  const continueOnWhatsApp = () => {
    let msg = "Hi LaunchBS, I'm interested in starting a project.";
    if (projectRef) msg += `\nInterested in: ${projectRef}`;
    if (business.trim()) msg += `\nBusiness: ${business.trim()}`;
    if (need.trim()) msg += `\nProject: ${need.trim()}`;
    if (logoUrl) msg += `\nLogo: ${logoUrl}`;
    if (photos.length) msg += `\nPhotos:\n${photos.join("\n")}`;
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="studio-budget-modal"
    >
      <div
        className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-gray-100 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5F5F5] hover:bg-[#EAEAEA] flex items-center justify-center text-[#687280] transition-colors"
          data-testid="studio-budget-close"
        >
          <X size={16} />
        </button>

        <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F] tracking-tight">Start Your Project</h2>
        {projectRef && <p className="text-xs text-[#B8901F] font-semibold mt-1">{projectRef}</p>}

        {/* Business name */}
        <label className="block text-sm font-semibold text-[#0F0F0F] mt-5">Business Name</label>
        <input
          value={business}
          onChange={(e) => setBusiness(e.target.value)}
          placeholder="Your business name"
          className="mt-2 w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]"
          data-testid="studio-business-input"
        />

        {/* Logo upload */}
        <label className="block text-sm font-semibold text-[#0F0F0F] mt-4">Upload Logo <span className="text-[#9CA3AF] font-normal">(optional)</span></label>
        <label className="mt-2 flex items-center gap-2 rounded-xl border border-dashed border-gray-300 px-3.5 py-2.5 text-sm text-[#687280] cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid="studio-logo-upload">
          {logoBusy ? <Loader2 size={16} className="animate-spin" /> : logoUrl ? <Check size={16} className="text-emerald-500" /> : <Upload size={16} className="text-[#B8901F]" />}
          {logoBusy ? "Uploading…" : logoUrl ? "Logo added" : "Choose logo image"}
          <input type="file" accept="image/*" className="hidden" onChange={handleLogo} />
        </label>
        {logoUrl && <img src={logoUrl} alt="logo" className="mt-2 h-12 w-12 rounded-lg object-cover border border-gray-100" />}

        {/* Photos upload */}
        <label className="block text-sm font-semibold text-[#0F0F0F] mt-4">Upload Photos <span className="text-[#9CA3AF] font-normal">(add at least 5 — optional)</span></label>
        <label className="mt-2 flex items-center gap-2 rounded-xl border border-dashed border-gray-300 px-3.5 py-2.5 text-sm text-[#687280] cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid="studio-photos-upload">
          {photoBusy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} className="text-[#B8901F]" />}
          {photoBusy ? "Uploading…" : photos.length ? `${photos.length} photo${photos.length > 1 ? "s" : ""} added — add more` : "Choose photos"}
          <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} />
        </label>
        {photos.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="studio-photo-thumbs">
            {photos.map((u, i) => (
              <img key={i} src={u} alt={`photo ${i + 1}`} className="h-10 w-10 rounded-md object-cover border border-gray-100" />
            ))}
          </div>
        )}

        <button
          onClick={continueOnWhatsApp}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white rounded-full py-3 text-sm font-bold transition-colors"
          data-testid="studio-continue-whatsapp"
        >
          <MessageCircle size={17} /> Continue on WhatsApp
        </button>
      </div>
    </div>
  );
};
